import type { DocumentData } from "firebase/firestore";
import { compactData, dateValue, stringValue } from "./firestore";
import {
  activeFields,
  fieldDefinitionFirestoreData,
  parseFieldDefinitions,
  type LeagueAthleteFieldDefinition,
} from "./fieldDefinition";
import {
  decodeDynamicValues,
  encodeDynamicValues,
  type LeagueDynamicFieldValue,
} from "./dynamicFieldValue";

// Ports of LeagueAthleteSchema / LeagueAthleteValue and the identically-shaped
// organization-admin variants. Contracts per firestore.rules
// validAthleteSchemaShape / validAthleteValueShape / validOrganizationAdminValueShape.

export interface LeagueFieldSchema {
  leagueID: string;
  sportID?: string;
  version: number;
  fields: LeagueAthleteFieldDefinition[];
  createdAt?: Date;
  updatedAt?: Date;
  updatedByUserID?: string;
}

export function parseFieldSchema(id: string, data: DocumentData): LeagueFieldSchema | null {
  const leagueID = stringValue(data.leagueID);
  if (!leagueID || leagueID !== id) return null;
  return {
    leagueID,
    sportID: stringValue(data.sportID),
    version: typeof data.version === "number" ? data.version : 0,
    fields: parseFieldDefinitions(data.fields),
    createdAt: dateValue(data.createdAt),
    updatedAt: dateValue(data.updatedAt),
    updatedByUserID: stringValue(data.updatedByUserID),
  };
}

export function fieldSchemaFirestoreData(schema: LeagueFieldSchema): Record<string, unknown> {
  return compactData({
    leagueID: schema.leagueID,
    version: schema.version,
    fields: [...schema.fields].sort((a, b) => a.sortOrder - b.sortOrder).map(fieldDefinitionFirestoreData),
    sportID: schema.sportID,
    createdAt: schema.createdAt,
    updatedAt: schema.updatedAt,
    updatedByUserID: schema.updatedByUserID,
  });
}

export function schemaActiveFields(schema: LeagueFieldSchema | null): LeagueAthleteFieldDefinition[] {
  return schema ? activeFields(schema.fields) : [];
}

export interface LeagueAthleteValue {
  id: string;
  leagueID: string;
  name: string;
  status: LeagueAthleteValueStatus;
  athleteUserID?: string;
  organizationID?: string;
  publicValues: Record<string, LeagueDynamicFieldValue>;
  organizationValues: Record<string, LeagueDynamicFieldValue>;
  leagueValues: Record<string, LeagueDynamicFieldValue>;
  createdByUserID?: string;
  createdAt?: Date;
  updatedAt?: Date;
  updatedByUserID?: string;
}

export type LeagueAthleteValueStatus = "draft" | "active" | "archived";

export const LEAGUE_ATHLETE_VALUE_STATUSES: LeagueAthleteValueStatus[] = ["draft", "active", "archived"];

export const LEAGUE_ATHLETE_VALUE_STATUS_LABELS: Record<LeagueAthleteValueStatus, string> = {
  draft: "Draft",
  active: "Active",
  archived: "Archived",
};

const ATHLETE_VALUE_STATUS_RANK: Record<LeagueAthleteValueStatus, number> = {
  draft: 0,
  active: 1,
  archived: 2,
};

export function athleteValueID(leagueID: string, seed = crypto.randomUUID().toLowerCase()): string {
  return `${leagueID}_athlete_${seed
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .join("-")}`;
}

export function parseAthleteValue(id: string, data: DocumentData): LeagueAthleteValue | null {
  const leagueID = stringValue(data.leagueID);
  const name = stringValue(data.name);
  const status = stringValue(data.status) as LeagueAthleteValueStatus | undefined;
  if (!leagueID || !name || !status || !LEAGUE_ATHLETE_VALUE_STATUSES.includes(status)) return null;
  return {
    id,
    leagueID,
    name,
    status,
    athleteUserID: stringValue(data.athleteUserID),
    organizationID: stringValue(data.organizationID),
    publicValues: decodeDynamicValues(data.publicValues),
    organizationValues: decodeDynamicValues(data.organizationValues),
    leagueValues: decodeDynamicValues(data.leagueValues),
    createdByUserID: stringValue(data.createdByUserID),
    createdAt: dateValue(data.createdAt),
    updatedAt: dateValue(data.updatedAt),
    updatedByUserID: stringValue(data.updatedByUserID),
  };
}

export function athleteValueFirestoreData(value: LeagueAthleteValue): Record<string, unknown> {
  return compactData({
    id: value.id,
    leagueID: value.leagueID,
    name: value.name,
    status: value.status,
    athleteUserID: value.athleteUserID,
    organizationID: value.organizationID,
    publicValues: encodeDynamicValues(value.publicValues),
    organizationValues: encodeDynamicValues(value.organizationValues),
    leagueValues: encodeDynamicValues(value.leagueValues),
    createdByUserID: value.createdByUserID,
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
    updatedByUserID: value.updatedByUserID,
  });
}

export function athleteValueAllValues(value: LeagueAthleteValue): Record<string, LeagueDynamicFieldValue> {
  return {
    ...value.leagueValues,
    ...value.organizationValues,
    ...value.publicValues,
  };
}

export function sortAthleteValues(first: LeagueAthleteValue, second: LeagueAthleteValue): number {
  const statusDelta = ATHLETE_VALUE_STATUS_RANK[first.status] - ATHLETE_VALUE_STATUS_RANK[second.status];
  if (statusDelta !== 0) return statusDelta;
  return first.name.localeCompare(second.name, undefined, { sensitivity: "base" });
}

export interface LeagueOrganizationAdminValue {
  id: string;
  leagueID: string;
  organizationID: string;
  adminUserID: string;
  publicValues: Record<string, LeagueDynamicFieldValue>;
  organizationValues: Record<string, LeagueDynamicFieldValue>;
  leagueValues: Record<string, LeagueDynamicFieldValue>;
  createdAt?: Date;
  updatedAt?: Date;
}

export function organizationAdminValueID(leagueID: string, organizationID: string, adminUserID: string): string {
  return `${leagueID}_${organizationID}_${adminUserID}`;
}

export function parseOrganizationAdminValue(id: string, data: DocumentData): LeagueOrganizationAdminValue | null {
  const leagueID = stringValue(data.leagueID);
  const organizationID = stringValue(data.organizationID);
  const adminUserID = stringValue(data.adminUserID);
  if (!leagueID || !organizationID || !adminUserID) return null;
  return {
    id,
    leagueID,
    organizationID,
    adminUserID,
    publicValues: decodeDynamicValues(data.publicValues),
    organizationValues: decodeDynamicValues(data.organizationValues),
    leagueValues: decodeDynamicValues(data.leagueValues),
    createdAt: dateValue(data.createdAt),
    updatedAt: dateValue(data.updatedAt),
  };
}

export function organizationAdminValueFirestoreData(value: LeagueOrganizationAdminValue): Record<string, unknown> {
  return compactData({
    id: value.id,
    leagueID: value.leagueID,
    organizationID: value.organizationID,
    adminUserID: value.adminUserID,
    publicValues: encodeDynamicValues(value.publicValues),
    organizationValues: encodeDynamicValues(value.organizationValues),
    leagueValues: encodeDynamicValues(value.leagueValues),
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
  });
}
